import { PlanOverviewDataFieldType } from '../../App/Components/Session/components/SessionDetails/components/AccountDetails/Components/PlanOverview/types';
import { utcDatePartsHandler } from './dateTimeHelpers';
import { SourceType } from '@store/sessions/types';

interface ClassNames {
	[className: string]: boolean;
}

export const getClasses = (classNames: ClassNames) => {
	const classes = [];
	for (const className in classNames) {
		if (classNames[className]) {
			classes.push(className);
		}
	}
	return classes.join(' ');
};

export const separateWords = (str: string): string => str.replace(/([a-z])([A-Z])/g, '$1 $2');
export const formatToTwoDigits = (value: number) => String(value).padStart(2, '0');

export const webSiteUrlResolver = (link: string) => {
	if (!link) return '-';
	if (link.includes('http')) return link;
	return `https://${link}`;
};

interface States {
	[key: string]: string;
}

export const generateAccountsClassName = (state: string, prefixClass: string) => {
	if (state.includes('left')) {
		return `${prefixClass}-days`;
	}
	const states: States = {
		'Past Due': 'past-due',
		'Usage Exceeded': 'exceeded',
		Disabled: 'disabled',
		'Special Case': 'special',
		Paused: 'paused',
		Deleted: 'deleted',
		Expired: 'expired',
		'Not Verified': 'not-verified',
		Removed: 'removed',
		Locked: 'locked',
	};
	const resultName = states[state] || '';
	return `${prefixClass}-${resultName}`;
};

export const upperCaseHandler = (val: string | number): string | number => {
	if (typeof val === 'string') {
		return val.slice(0, 1).toUpperCase() + val.slice(1);
	}
	return val;
};

export const orphanFileRowHandler = (data: PlanOverviewDataFieldType): string | boolean => {
	if (data === 'A') return 'Automatic';
	if (data === 'M') return 'Manual';
	return false;
};

export const addSpacesInNumbers = (str: string, delimiter: string = ',') => {
	const regExp = /\B(?=(\d{3})+(?!\d))/g;

	if (str.length > 3) {
		return str.replace(regExp, delimiter);
	}
	return str;
};

export const splitIntoInitials = (initials: string) => {
	const dividedName = initials.split(' ');
	const firstLetters = dividedName.map(parts => parts[0]);
	return firstLetters.join('');
};

/*Helper to split the string by some character*/

export const splitTextInSpansByChar = (text: string, char: string) => {
	const arr = text.split(char);
	return arr.map((item, index) => {
		if (index === 0) return item;
		return char + item;
	});
};

export const deleteSpaces = (value: string) => value.replace(/ /gi, '');
export const deleteUnderscores = (value: string) => value.replace(/_/gi, ' ');

// Handlers to parse OData options string for AgGrid sheets

const convertOdataDatesToUTCHandler = (options: string) => {
	const separator = 'T00:00:00.000Z';
	const datePartLength = -10;

	const optionsArr = options.split(separator);
	const lastItemIndex = optionsArr.length;

	const convertedOptionsArr = optionsArr.map((item, i) => {
		const dateStr = item.slice(datePartLength);
		if (i + 1 === lastItemIndex) return item;

		const { seconds, minutes, hours, day, month, year } = item.includes(' ge ')
			? utcDatePartsHandler(dateStr, ' 00:00:00')
			: utcDatePartsHandler(dateStr, ' 23:59:59');

		const convertedDateStr = `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.000Z`;
		const formattedItem = item.slice(0, item.length + datePartLength).concat(convertedDateStr);

		return formattedItem;
	});

	const resultOptions = convertedOptionsArr.join('');
	return resultOptions;
};

export const replaceRequestOptionsHandler = (options: string) => {
	const optionsWithCount = options.includes('?$count=true') ? options : options.replace('?', '?$count=true&');

	return convertOdataDatesToUTCHandler(optionsWithCount)
		.replace(/'true'/gi, 'true')
		.replace(/'false'/gi, 'false')
		.replace(/\+/gi, '%2b')
		.replace('/aggregate($count as count)', '');
};

export const convertHoursToShortDate = (hours: number) => {
	const convertedMinuts = hours % 60;
	const convertedHours = (hours - convertedMinuts) / 60;

	return `${convertedHours}h ${convertedMinuts}m`;
};

export const sourceIconHandler = (value: string) => {
	switch (value) {
		case SourceType.Accounts:
			return 'list';
		case SourceType.Chat:
			return 'chat';
		case SourceType.Phone:
			return 'phone';
		case SourceType.SupportTicket:
			return 'task-check';
		case SourceType.History:
			return 'archive';
		case SourceType.Share:
			return 'share';
		case SourceType.InternalApp:
			return 'document-attachment';
		case SourceType.Unknown:
		default:
			return '';
	}
};

enum BooleanString {
	TRUE = 'true',
	FALSE = 'false',
}

export const checkTextOnBooleanHandler = (text: BooleanString | string) => {
	switch (text) {
		case BooleanString.TRUE:
			return true;
		case BooleanString.FALSE:
			return false;
		default:
			return text;
	}
};

export const convertDaysToLogString = (value: number | null): string => {
    switch(value) {
        case null: return 'Never expire';
        case 1: return "1 day";
        case 3: return "3 days";
        case 5: return "5 days";
        case 7: return "1 week (7 days)";
        case 14: return "2 weeks (14 days)";
        case 30: return "1 month (30 days)";
        case 60: return "2 months (60 days)";
        case 90: return "3 months (90 days)";
        case 180: return "6 months (180 days)";
        case 365: return "12 months (365 days)";
        case 400: return "13 months (400 days)";
        case 545: return "18 months (545 days)";
        case 731: return "2 years (731 days)";
        case 1827: return "5 years (1,827 days)";
        case 3653: return "10 years (3,653 days)";
        default: return `${addSpacesInNumbers(String(value))} days`;
    }
}